import { useRef, useEffect } from "react";
import RoadmapListRow from "./RoadmapListRow";

/**
 * RoadmapListPane — left column. Renders the filtered list of unified roadmap
 * items as scrollable rows. Auto-scrolls the selected row into view when it
 * changes (e.g. via URL deep-link).
 *
 * Phase 12E (cycle-5c+25-fix8).
 */
export default function RoadmapListPane({
  items,
  totalCount,
  selectedId,
  onSelect,
  hasActiveFilters,
  onClearFilters,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!selectedId || !containerRef.current) return;
    const row = containerRef.current.querySelector(
      `[data-testid="roadmap-list-row-${CSS.escape(selectedId)}"]`,
    );
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedId]);

  return (
    <div
      className="surface-card flex flex-col responsive-list-pane"
      data-testid="roadmap-list-pane"
    >
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: "hsl(var(--border))" }}
      >
        <span
          className="text-mono"
          style={{
            fontSize: "0.65rem",
            color: "hsl(var(--muted-foreground))",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            fontWeight: 700,
          }}
        >
          {items.length} of {totalCount} items{hasActiveFilters ? " · filtered" : ""}
        </span>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-mono"
            style={{
              fontSize: "0.65rem",
              color: "hsl(var(--muted-foreground))",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
            }}
            data-testid="roadmap-list-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-2 space-y-2"
        data-testid="roadmap-list-scroll"
      >
        {items.length === 0 ? (
          <div className="text-center py-12">
            <p
              className="text-xs"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              {hasActiveFilters
                ? "No items match the current filters."
                : "No items."}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={onClearFilters}
                className="text-mono mt-2"
                style={{
                  fontSize: "0.65rem",
                  color: "hsl(var(--primary))",
                  textDecoration: "underline",
                }}
              >
                Clear filters to see all items
              </button>
            )}
          </div>
        ) : (
          items.map((item) => (
            <RoadmapListRow
              key={item.id}
              item={item}
              isSelected={item.id === selectedId}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}
